import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowLeft, FileText, Lock, Unlock, Phone, Mail, HelpCircle, AlertCircle, Send } from 'lucide-react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const Instruktioner = () => {
  useDocumentMetadata({
    title: 'Instruktioner & SharePoint | MedicCare',
    description: 'Få adgang til vores officielle vejledninger, instrukser og dokumentation på SharePoint. Kontakt Johnny Andersen for adgangskode.'
  });

  const [password, setPassword] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [formStatus, setFormStatus] = useState(null);

  // Check if already unlocked in this session
  useEffect(() => {
    const unlocked = localStorage.getItem('mediccare_sharepoint_unlocked');
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    const cleanPassword = password.trim();
    
    // Accept only the exact password (case-sensitive)
    if (cleanPassword === 'MC-Instrukser!' || cleanPassword === 'MC-Instukser!') {
      setIsUnlocked(true);
      setPasswordError('');
      localStorage.setItem('mediccare_sharepoint_unlocked', 'true');
    } else {
      setPasswordError('Forkert adgangskode. Kontakt venligst Johnny Andersen for at få den korrekte adgangskode.');
    }
  };

  const handleLock = () => {
    setIsUnlocked(false);
    setPassword('');
    localStorage.removeItem('mediccare_sharepoint_unlocked');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 py-20 lg:py-28 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/shutterstock_2684544319.jpg" 
            alt="MedicCare Instruktioner" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-[#2c3223]/85 mix-blend-multiply"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-soft hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={14} /> Tilbage til Forsiden
            </Link>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
              Instruktioner & SharePoint
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-0 leading-relaxed">
              Her finder du adgang til vores officielle vejledninger, instrukser og dokumentation. Vi har opdelt vores materiale i en åben og en låst sektion for at sikre korrekt og fortrolig håndtering.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Intro text */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-block px-4 py-1.5 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
              Dokumentation & Vejledninger
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900 mb-6">
              Adgang til vores SharePoint-mapper
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              For at understøtte kommuner, apoteker og plejeperonale bedst muligt, stiller vi alt relevant materiale til rådighed via SharePoint. Nedenfor kan du tilgå vores åbne mappe eller låse op for vores fortrolige instrukser.
            </p>
          </div>

          {/* SharePoint Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
            
            {/* Card 1: Open Folder */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 flex flex-col justify-between transition-all hover:shadow-xl">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 rounded-xl bg-brand-soft text-[#2c3223] flex items-center justify-center">
                    <i className="fa-solid fa-folder-open text-2xl" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
                    Offentlig adgang
                  </span>
                </div>
                
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Deling Alle
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Denne SharePoint-mappe er frit tilgængelig for alle. Her finder du generelle produktbeskrivelser, foldere, e-læringsmateriale og introduktionsvideoer til vores løsninger.
                </p>

                <ul className="space-y-3 mb-8 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" />
                    <span>Produktbrochurer og foldere</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" />
                    <span>Introduktionsvideoer og cases</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" />
                    <span>Generelt e-læringsmateriale</span>
                  </li>
                </ul>
              </div>

              <a 
                href="https://mediccaredk.sharepoint.com/:f:/s/FileArea/IgBaWpuKAefySpVVUp3EptbIAXr_iK3uCtxdlPoYAkaNr7w?e=Wg5GUS" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full bg-[#2c3223] text-white text-center text-xs font-bold uppercase tracking-widest py-4 px-6 rounded-md hover:bg-brand-dark transition-all flex items-center justify-center gap-2 shadow-sm"
              >
                <i className="fa-solid fa-arrow-up-right-from-square" />
                Åbn Deling Alle
              </a>
            </div>

            {/* Card 2: Locked Folder */}
            <div className={`bg-white rounded-2xl shadow-lg border p-8 flex flex-col justify-between transition-all ${isUnlocked ? 'border-emerald-500 shadow-emerald-100/50' : 'border-slate-200'}`}>
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${isUnlocked ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {isUnlocked ? (
                      <i className="fa-solid fa-folder-open text-2xl" />
                    ) : (
                      <i className="fa-solid fa-folder-lock text-2xl" />
                    )}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full transition-colors ${isUnlocked ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {isUnlocked ? 'Låst op' : 'Kræver adgangskode'}
                  </span>
                </div>
                
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Deling Instrukser
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Denne SharePoint-mappe indeholder fortrolige kliniske vejledninger, tekniske instrukser, systemmanualer til FlexSuite og TIM samt driftsinstrukser.
                </p>

                {!isUnlocked ? (
                  <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4 mb-6">
                    <p className="text-xs text-amber-900 leading-relaxed flex gap-2">
                      <AlertCircle size={16} className="shrink-0 text-amber-700 mt-0.5" />
                      <span>
                        Du skal have adgang til SharePoint. For at få adgangskoden bedes du kontakte Johnny Andersen på tlf. <strong>+45 40 83 08 30</strong>, e-mail <strong>info@mediccare.dk</strong> eller udfylde formularen herunder.
                      </span>
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-3 mb-8 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-emerald-600" />
                      <span>Kliniske vejledninger & instrukser</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-emerald-600" />
                      <span>Tekniske manualer til FlexSuite & TIM</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-emerald-600" />
                      <span>Driftsvejledninger for sundhedspersonale</span>
                    </li>
                  </ul>
                )}
              </div>

              {isUnlocked ? (
                <div className="space-y-3">
                  <a 
                    href="https://mediccaredk.sharepoint.com/:f:/s/FileArea/IgC4ececYmiLQ5Alv6QrqRVmAS339tQ5Pv6zFE6-hCAGcfI?e=28ewLZ" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full bg-emerald-600 text-white text-center text-xs font-bold uppercase tracking-widest py-4 px-6 rounded-md hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 shadow-sm"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" />
                    Åbn Deling Instrukser
                  </a>
                  <button 
                    onClick={handleLock}
                    className="w-full text-slate-500 hover:text-slate-800 text-xs font-semibold py-2 transition-colors flex items-center justify-center gap-1"
                  >
                    <Lock size={12} /> Lås mappen igen
                  </button>
                </div>
              ) : (
                <form onSubmit={handleUnlock} className="space-y-3">
                  <div className="relative">
                    <input 
                      type="password" 
                      placeholder="Indtast adgangskode" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 py-3 pl-10 pr-4 rounded-md focus:outline-none focus:border-[#2c3223] transition-colors text-sm"
                    />
                    <div className="absolute left-3.5 top-3.5 text-slate-400">
                      <Lock size={16} />
                    </div>
                  </div>
                  {passwordError && (
                    <p className="text-xs text-red-600 font-medium">{passwordError}</p>
                  )}
                  <button 
                    type="submit"
                    className="w-full bg-brand-light text-white text-xs font-bold uppercase tracking-widest py-3.5 px-6 rounded-md hover:bg-[#2c3223] transition-all flex items-center justify-center gap-2 shadow-xs"
                  >
                    <Unlock size={14} />
                    Lås op
                  </button>
                </form>
              )}
            </div>

          </div>

          {/* Contact & Request Form Section */}
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            
            {/* Contact Info Column */}
            <div className="lg:col-span-5 bg-slate-900 p-8 lg:p-12 text-white flex flex-col justify-between relative">
              <div className="absolute inset-0 z-0 opacity-10">
                <img src="/shutterstock_2684544319.jpg" alt="" className="w-full h-full object-cover" />
              </div>
              
              <div className="relative z-10">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-light">Direkte kontakt</span>
                <h3 className="text-2xl lg:text-3xl font-extrabold mt-3 mb-6">Kontakt Johnny Andersen</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-8">
                  Har du brug for adgangskoden til vores fortrolige instrukser, eller har du spørgsmål til vores SharePoint-struktur? Kontakt os direkte, så hjælper vi dig i gang med det samme.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-brand-light shrink-0">
                      <Phone size={18} />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Ring direkte</div>
                      <a href="tel:+4540830830" className="text-base font-bold text-white hover:text-brand-light transition-colors">
                        +45 40 83 08 30
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-brand-light shrink-0">
                      <Mail size={18} />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Send en e-mail</div>
                      <a href="mailto:info@mediccare.dk" className="text-base font-bold text-white hover:text-brand-light transition-colors">
                        info@mediccare.dk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-brand-light shrink-0">
                      <i className="fa-solid fa-user-tie text-lg text-brand-light" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Kontaktperson</div>
                      <div className="text-base font-bold text-white">
                        Johnny Andersen, CMO
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative z-10 pt-8 border-t border-white/10 mt-8 text-xs text-slate-400">
                MedicCare ApS • CVR: 38871234 • Support 24/7
              </div>
            </div>

            {/* Request Form Column */}
            <div className="lg:col-span-7 p-8 lg:p-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Anmod om adgangskode</h3>
              <p className="text-slate-500 text-sm mb-8">
                Udfyld formularen nedenfor for at anmode om adgangskoden til mappen 'Deling Instrukser'. Vi behandler din anmodning hurtigst muligt.
              </p>

              {formStatus === 'success' ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="bg-emerald-100 p-4 rounded-full mb-6 text-emerald-800">
                    <CheckCircle2 size={40} />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-2">Anmodning modtaget!</h4>
                  <p className="text-slate-600 max-w-md">
                    Tak for din henvendelse. Johnny vil behandle din anmodning og sende dig adgangskoden hurtigst muligt.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmitForm} className="space-y-6">
                  {/* Honeypot field */}
                  <input 
                    type="text" 
                    name="website_url" 
                    className="sr-only absolute -top-999 left-0" 
                    tabIndex="-1" 
                    autoComplete="off" 
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Navn</label>
                      <input 
                        required 
                        name="name" 
                        type="text" 
                        placeholder="Dit fulde navn" 
                        className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors text-sm" 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">E-mail</label>
                      <input 
                        required 
                        name="email" 
                        type="email" 
                        placeholder="din@email.dk" 
                        className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors text-sm" 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Telefonnummer</label>
                      <input 
                        required 
                        name="phone" 
                        type="tel" 
                        placeholder="+45 00 00 00 00" 
                        className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors text-sm" 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Kommune / Organisation</label>
                      <input 
                        required 
                        name="organization" 
                        type="text" 
                        placeholder="F.eks. Aarhus Kommune" 
                        className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors text-sm" 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Emne</label>
                    <input 
                      required 
                      name="subject" 
                      type="text" 
                      defaultValue="Anmodning om adgangskode til SharePoint (Deling Instrukser)" 
                      className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors text-sm" 
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Besked</label>
                    <textarea 
                      required 
                      name="message" 
                      rows="4" 
                      placeholder="Skriv din besked her, f.eks. hvorfor du har brug for adgang til instrukserne..." 
                      className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors text-sm resize-none"
                    ></textarea>
                  </div>

                  {formStatus === 'error' && (
                    <p className="text-xs text-red-600 font-medium">Der skete en fejl under afsendelsen. Prøv venligst igen eller ring direkte.</p>
                  )}

                  <button 
                    type="submit" 
                    className="w-full bg-[#2c3223] text-white text-xs font-bold uppercase tracking-widest py-4 px-6 rounded-md hover:bg-brand-dark transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer"
                  >
                    <Send size={14} />
                    Send anmodning
                  </button>
                </form>
              )}
            </div>

          </div>

        </div>
      </section>
    </div>
  );
};

export default Instruktioner;
